import {
  ACSIsolationProfile,
  ACSRuntimeStatus,
  ACSRuntimeType,
  AssetStatus,
  AssetType,
  DebentureStatus,
  DebentureType,
  FederationLevel,
  FederationStatus,
  FundingStatus,
  FundingType,
  IdentityStatus,
  IdentityType,
  PluginStatus,
  PluginType,
  ProjectStatus,
  ProjectType,
  RequestStatus,
  RequestType,
  RevenueSource,
  RevenueStatus,
  RiskTier,
  TelemetryEventType,
  TelemetrySeverity,
  TreasuryExposureStatus,
  TreasuryExposureType,
  VerificationLevel
} from "../types/business.enums.js";
import type { BusinessRuntimeDataset, GovernanceReference, TelemetryProfile } from "../types/business.types.js";

const now = "2026-05-14T12:00:00.000Z";

const governanceCompatible: GovernanceReference = {
  proposalId: "gov-ref-business-sprint-1",
  proposalUrl: "mock://governance/business-runtime-foundation",
  constitutionalCompatibility: "REVIEW_REQUIRED",
  treasuryRestrictions: ["NO_TREASURY_MOVEMENT", "NO_GOVERNANCE_EXECUTION", "MOCK_ONLY"]
};

const telemetryProfile = (profileId: string, visibility: TelemetryProfile["visibility"]): TelemetryProfile => ({
  profileId,
  enabled: true,
  visibility,
  requiredEvents: [
    TelemetryEventType.PROJECT_STATUS_CHANGED,
    TelemetryEventType.TREASURY_EXPOSURE_UPDATED,
    TelemetryEventType.ACS_RUNTIME_UPDATED
  ]
});

export const businessRuntimeMock: BusinessRuntimeDataset = {
  identities: [
    {
      id: "id-axodus-core",
      identityType: IdentityType.REQUESTER,
      displayName: "Axodus Core",
      contactReference: "mock://contacts/axodus-core",
      federationLevel: FederationLevel.LEVEL_5_AXODUS_NATIVE_ENTITY,
      verificationLevel: VerificationLevel.FEDERATION_APPROVED,
      status: IdentityStatus.OPERATIONAL,
      serviceScope: ["internal-products", "treasury-sponsored-assets", "runtime-foundation"],
      createdAt: now,
      updatedAt: now
    },
    {
      id: "id-harmony-dao",
      identityType: IdentityType.DAO,
      displayName: "Harmony DAO",
      walletReference: "mock://wallets/harmony-dao-treasury",
      federationLevel: FederationLevel.LEVEL_3_FEDERATED_DAO,
      verificationLevel: VerificationLevel.GOVERNANCE_VERIFIED,
      status: IdentityStatus.OPERATIONAL,
      serviceScope: ["governance-plugins", "dao-operations"],
      createdAt: now,
      updatedAt: now
    },
    {
      id: "id-enterprise-sample",
      identityType: IdentityType.ENTERPRISE,
      displayName: "Sample Enterprise ACS Client",
      contactReference: "mock://contacts/enterprise-acs-client",
      federationLevel: FederationLevel.LEVEL_1_VERIFIED_REQUESTER,
      verificationLevel: VerificationLevel.BASIC_VERIFIED,
      status: IdentityStatus.VERIFIED,
      serviceScope: ["enterprise-acs", "private-development"],
      createdAt: now,
      updatedAt: now
    },
    {
      id: "id-mazikeen-agent",
      identityType: IdentityType.AGENT,
      displayName: "Mazikeen",
      federationLevel: FederationLevel.LEVEL_0_EXTERNAL_REQUESTER,
      verificationLevel: VerificationLevel.UNVERIFIED,
      status: IdentityStatus.CREATED,
      serviceScope: ["mock-personal-assistant-reference"],
      createdAt: now,
      updatedAt: now
    },
    {
      id: "id-mariana-agent",
      identityType: IdentityType.AGENT,
      displayName: "Mariana (Ana)",
      federationLevel: FederationLevel.LEVEL_0_EXTERNAL_REQUESTER,
      verificationLevel: VerificationLevel.UNVERIFIED,
      status: IdentityStatus.CREATED,
      serviceScope: ["mock-personal-assistant-reference"],
      createdAt: now,
      updatedAt: now
    },
    {
      id: "id-redhat-dev-agent",
      identityType: IdentityType.AGENT,
      displayName: "RedHat Dev",
      federationLevel: FederationLevel.LEVEL_0_EXTERNAL_REQUESTER,
      verificationLevel: VerificationLevel.UNVERIFIED,
      status: IdentityStatus.CREATED,
      serviceScope: ["mock-development-assistant-reference"],
      createdAt: now,
      updatedAt: now
    }
  ],
  federationParticipants: [
    {
      id: "fed-axodus-core",
      identityId: "id-axodus-core",
      displayName: "Axodus Core",
      level: FederationLevel.LEVEL_5_AXODUS_NATIVE_ENTITY,
      status: FederationStatus.OPERATIONAL,
      permissions: ["REQUEST_INTERNAL_PRODUCTS", "VIEW_TREASURY_EXPOSURE", "VIEW_RUNTIME_TELEMETRY"],
      governanceReference: governanceCompatible,
      createdAt: now,
      updatedAt: now
    },
    {
      id: "fed-harmony-dao",
      identityId: "id-harmony-dao",
      displayName: "Harmony DAO",
      level: FederationLevel.LEVEL_3_FEDERATED_DAO,
      status: FederationStatus.UNDER_REVIEW,
      permissions: ["REQUEST_DAO_PLUGINS", "VIEW_DAO_PLUGIN_STATUS"],
      governanceReference: governanceCompatible,
      createdAt: now,
      updatedAt: now
    }
  ],
  requests: [
    {
      id: "req-governance-country",
      title: "Governance.Country runtime visibility",
      description: "Mock intake for Axodus governance platform operational visibility.",
      requesterId: "id-axodus-core",
      requesterType: IdentityType.REQUESTER,
      requestType: RequestType.INTERNAL_AXODUS_PRODUCT,
      status: RequestStatus.APPROVED,
      priority: "CRITICAL",
      estimatedBudget: { amount: 90000, currency: "USD" },
      preferredFundingModel: FundingType.TREASURY,
      governanceRequired: true,
      treasuryExposureExpected: true,
      acsRequired: true,
      pluginRequired: false,
      createdAt: now,
      updatedAt: now
    },
    {
      id: "req-enterprise-acs",
      title: "Sample enterprise ACS request",
      description: "Private enterprise ACS visibility request with dedicated isolation, no provisioning.",
      requesterId: "id-enterprise-sample",
      requesterType: IdentityType.ENTERPRISE,
      requestType: RequestType.ENTERPRISE_DEVELOPMENT,
      status: RequestStatus.UNDER_REVIEW,
      priority: "HIGH",
      estimatedBudget: { amount: 45000, currency: "USD" },
      preferredFundingModel: FundingType.DIRECT,
      governanceRequired: true,
      treasuryExposureExpected: false,
      acsRequired: true,
      pluginRequired: false,
      createdAt: now,
      updatedAt: now
    },
    {
      id: "req-harmony-rp-voting",
      title: "Harmony RP Voting plugin",
      description: "DAO plugin request for Harmony RP voting lifecycle tracking.",
      requesterId: "id-harmony-dao",
      requesterType: IdentityType.DAO,
      requestType: RequestType.DAO_INFRASTRUCTURE,
      status: RequestStatus.GOVERNANCE_REVIEW,
      priority: "HIGH",
      estimatedBudget: { amount: 32000, currency: "USD" },
      preferredFundingModel: FundingType.HYBRID,
      governanceRequired: true,
      treasuryExposureExpected: true,
      acsRequired: false,
      pluginRequired: true,
      createdAt: now,
      updatedAt: now
    }
  ],
  projects: [
    {
      id: "proj-governance-country",
      requestId: "req-governance-country",
      title: "Governance.Country",
      projectType: ProjectType.INTERNAL_PRODUCT,
      status: ProjectStatus.ACTIVE,
      ownerId: "id-axodus-core",
      governanceReference: governanceCompatible,
      fundingId: "fund-governance-country",
      assetId: "asset-governance-country",
      riskTier: RiskTier.TIER_1_CRITICAL_INFRASTRUCTURE,
      milestones: [{ id: "mil-gov-1", label: "Runtime visibility foundation", status: "COMPLETED" }],
      telemetryProfile: telemetryProfile("tp-governance-country", "PUBLIC"),
      createdAt: now,
      updatedAt: now
    },
    {
      id: "proj-vault-country",
      requestId: "req-governance-country",
      title: "Vault.Country",
      projectType: ProjectType.TREASURY_SPONSORED_ASSET,
      status: ProjectStatus.MAINTENANCE,
      ownerId: "id-axodus-core",
      governanceReference: governanceCompatible,
      fundingId: "fund-vault-country",
      assetId: "asset-vault-country",
      riskTier: RiskTier.TIER_1_CRITICAL_INFRASTRUCTURE,
      milestones: [{ id: "mil-vault-1", label: "Recovery vault status visibility", status: "ACTIVE" }],
      telemetryProfile: telemetryProfile("tp-vault-country", "PUBLIC"),
      createdAt: now,
      updatedAt: now
    },
    {
      id: "proj-dex-country",
      requestId: "req-governance-country",
      title: "Dex.Country",
      projectType: ProjectType.PUBLIC_INFRASTRUCTURE,
      status: ProjectStatus.ACTIVE,
      ownerId: "id-axodus-core",
      governanceReference: governanceCompatible,
      fundingId: "fund-dex-country",
      assetId: "asset-dex-country",
      riskTier: RiskTier.TIER_2_ECOSYSTEM_STRATEGIC,
      milestones: [{ id: "mil-dex-1", label: "Aggregator runtime visibility", status: "ACTIVE" }],
      telemetryProfile: telemetryProfile("tp-dex-country", "PUBLIC"),
      createdAt: now,
      updatedAt: now
    },
    {
      id: "proj-harmony-rp-voting",
      requestId: "req-harmony-rp-voting",
      title: "Harmony RP Voting plugin",
      projectType: ProjectType.DAO_PLUGIN,
      status: ProjectStatus.GOVERNANCE_REVIEW,
      ownerId: "id-harmony-dao",
      governanceReference: governanceCompatible,
      fundingId: "fund-harmony-rp-voting",
      assetId: "asset-harmony-rp-voting",
      riskTier: RiskTier.TIER_3_REVENUE_GENERATING,
      milestones: [{ id: "mil-rp-1", label: "Plugin constitutional compatibility review", status: "ACTIVE" }],
      telemetryProfile: telemetryProfile("tp-harmony-rp", "DAO_SCOPED"),
      createdAt: now,
      updatedAt: now
    },
    {
      id: "proj-enterprise-acs",
      requestId: "req-enterprise-acs",
      title: "Sample enterprise ACS runtime",
      projectType: ProjectType.ENTERPRISE_ACS,
      status: ProjectStatus.EVALUATING,
      ownerId: "id-enterprise-sample",
      governanceReference: governanceCompatible,
      fundingId: "fund-enterprise-acs",
      assetId: "asset-enterprise-acs",
      riskTier: RiskTier.TIER_3_REVENUE_GENERATING,
      milestones: [{ id: "mil-enterprise-1", label: "Isolation profile review", status: "PENDING" }],
      telemetryProfile: telemetryProfile("tp-enterprise-acs", "ENTERPRISE_SCOPED"),
      createdAt: now,
      updatedAt: now
    }
  ],
  assets: [
    {
      id: "asset-governance-country",
      name: "Governance.Country",
      assetType: AssetType.INTERNAL_AXODUS_ASSET,
      ownerType: IdentityType.REQUESTER,
      ownerId: "id-axodus-core",
      status: AssetStatus.ACTIVE,
      projectId: "proj-governance-country",
      fundingId: "fund-governance-country",
      revenueModel: RevenueSource.TREASURY_ASSET,
      maintenanceOwner: "id-axodus-core",
      telemetryProfile: telemetryProfile("tp-asset-governance-country", "PUBLIC"),
      createdAt: now,
      updatedAt: now
    },
    {
      id: "asset-vault-country",
      name: "Vault.Country",
      assetType: AssetType.PUBLIC_INFRASTRUCTURE,
      ownerType: IdentityType.REQUESTER,
      ownerId: "id-axodus-core",
      status: AssetStatus.MAINTENANCE,
      projectId: "proj-vault-country",
      fundingId: "fund-vault-country",
      revenueModel: RevenueSource.INFRASTRUCTURE,
      maintenanceOwner: "id-axodus-core",
      telemetryProfile: telemetryProfile("tp-asset-vault-country", "PUBLIC"),
      createdAt: now,
      updatedAt: now
    },
    {
      id: "asset-dex-country",
      name: "Dex.Country",
      assetType: AssetType.PUBLIC_INFRASTRUCTURE,
      ownerType: IdentityType.REQUESTER,
      ownerId: "id-axodus-core",
      status: AssetStatus.ACTIVE,
      projectId: "proj-dex-country",
      fundingId: "fund-dex-country",
      revenueModel: RevenueSource.INFRASTRUCTURE,
      maintenanceOwner: "id-axodus-core",
      telemetryProfile: telemetryProfile("tp-asset-dex-country", "PUBLIC"),
      createdAt: now,
      updatedAt: now
    },
    {
      id: "asset-harmony-rp-voting",
      name: "Harmony RP Voting plugin",
      assetType: AssetType.DAO_ASSET,
      ownerType: IdentityType.DAO,
      ownerId: "id-harmony-dao",
      status: AssetStatus.UNDER_REVIEW,
      projectId: "proj-harmony-rp-voting",
      fundingId: "fund-harmony-rp-voting",
      revenueModel: RevenueSource.PLUGIN,
      maintenanceOwner: "id-axodus-core",
      telemetryProfile: telemetryProfile("tp-asset-harmony-rp", "DAO_SCOPED"),
      createdAt: now,
      updatedAt: now
    },
    {
      id: "asset-enterprise-acs",
      name: "Sample enterprise ACS runtime",
      assetType: AssetType.ENTERPRISE_ASSET,
      ownerType: IdentityType.ENTERPRISE,
      ownerId: "id-enterprise-sample",
      status: AssetStatus.PROPOSED,
      projectId: "proj-enterprise-acs",
      fundingId: "fund-enterprise-acs",
      revenueModel: RevenueSource.SUBSCRIPTION,
      maintenanceOwner: "id-axodus-core",
      telemetryProfile: telemetryProfile("tp-asset-enterprise-acs", "ENTERPRISE_SCOPED"),
      createdAt: now,
      updatedAt: now
    }
  ],
  fundingRecords: [
    {
      id: "fund-governance-country",
      projectId: "proj-governance-country",
      assetId: "asset-governance-country",
      fundingType: FundingType.TREASURY,
      status: FundingStatus.EXECUTION,
      targetAmount: 90000,
      raisedAmount: 90000,
      currency: "USD",
      issuerId: "id-axodus-core",
      treasuryExposureId: "tex-governance-country",
      createdAt: now,
      updatedAt: now
    },
    {
      id: "fund-vault-country",
      projectId: "proj-vault-country",
      assetId: "asset-vault-country",
      fundingType: FundingType.TREASURY,
      status: FundingStatus.REVENUE_PHASE,
      targetAmount: 120000,
      raisedAmount: 120000,
      currency: "USD",
      issuerId: "id-axodus-core",
      treasuryExposureId: "tex-vault-country",
      createdAt: now,
      updatedAt: now
    },
    {
      id: "fund-dex-country",
      projectId: "proj-dex-country",
      assetId: "asset-dex-country",
      fundingType: FundingType.DEBENTURE,
      status: FundingStatus.FUNDING_ACTIVE,
      targetAmount: 150000,
      raisedAmount: 87500,
      currency: "USD",
      issuerId: "id-axodus-core",
      treasuryExposureId: "tex-dex-country",
      debentureId: "deb-dex-country",
      createdAt: now,
      updatedAt: now
    },
    {
      id: "fund-harmony-rp-voting",
      projectId: "proj-harmony-rp-voting",
      assetId: "asset-harmony-rp-voting",
      fundingType: FundingType.HYBRID,
      status: FundingStatus.GOVERNANCE_REVIEW,
      targetAmount: 32000,
      raisedAmount: 0,
      currency: "USD",
      issuerId: "id-harmony-dao",
      treasuryExposureId: "tex-harmony-rp-voting",
      createdAt: now,
      updatedAt: now
    },
    {
      id: "fund-enterprise-acs",
      projectId: "proj-enterprise-acs",
      assetId: "asset-enterprise-acs",
      fundingType: FundingType.DIRECT,
      status: FundingStatus.DRAFT,
      targetAmount: 45000,
      raisedAmount: 0,
      currency: "USD",
      issuerId: "id-enterprise-sample",
      createdAt: now,
      updatedAt: now
    }
  ],
  debentures: [
    {
      id: "deb-dex-country",
      projectId: "proj-dex-country",
      assetId: "asset-dex-country",
      issuerId: "id-axodus-core",
      debentureType: DebentureType.NON_CONVERTIBLE,
      convertible: false,
      targetAmount: 150000,
      raisedAmount: 87500,
      currency: "USD",
      aprModel: "mock-fixed-rate-review-required",
      maturityDate: "2027-05-14T00:00:00.000Z",
      repaymentSource: RevenueSource.INFRASTRUCTURE,
      status: DebentureStatus.ACTIVE_OFFERING,
      createdAt: now,
      updatedAt: now
    }
  ],
  treasuryExposures: [
    {
      id: "tex-governance-country",
      projectId: "proj-governance-country",
      assetId: "asset-governance-country",
      exposureType: TreasuryExposureType.DIRECT_TREASURY_ALLOCATION,
      riskTier: RiskTier.TIER_1_CRITICAL_INFRASTRUCTURE,
      requestedAmount: 90000,
      approvedAmount: 90000,
      consumedAmount: 62000,
      currency: "USD",
      treasurySource: "mock://treasury/axodus-core",
      expectedReturn: "governance continuity and ecosystem legitimacy",
      repaymentModel: "treasury-owned infrastructure",
      status: TreasuryExposureStatus.CONSUMING,
      governanceReference: governanceCompatible,
      createdAt: now,
      updatedAt: now
    },
    {
      id: "tex-vault-country",
      projectId: "proj-vault-country",
      assetId: "asset-vault-country",
      exposureType: TreasuryExposureType.TREASURY_SPONSORSHIP,
      riskTier: RiskTier.TIER_1_CRITICAL_INFRASTRUCTURE,
      requestedAmount: 120000,
      approvedAmount: 120000,
      consumedAmount: 70000,
      currency: "USD",
      treasurySource: "mock://treasury/axodus-core",
      expectedReturn: "recovery infrastructure resilience",
      repaymentModel: "future infrastructure fees",
      status: TreasuryExposureStatus.ACTIVE,
      governanceReference: governanceCompatible,
      createdAt: now,
      updatedAt: now
    },
    {
      id: "tex-dex-country",
      projectId: "proj-dex-country",
      assetId: "asset-dex-country",
      exposureType: TreasuryExposureType.DEBENTURE_EXPOSURE,
      riskTier: RiskTier.TIER_2_ECOSYSTEM_STRATEGIC,
      requestedAmount: 150000,
      approvedAmount: 150000,
      consumedAmount: 40000,
      currency: "USD",
      treasurySource: "mock://treasury/debenture-sponsor",
      expectedReturn: "aggregator fee revenue visibility",
      repaymentModel: "debenture-backed revenue phase",
      status: TreasuryExposureStatus.ALLOCATED,
      governanceReference: governanceCompatible,
      createdAt: now,
      updatedAt: now
    },
    {
      id: "tex-harmony-rp-voting",
      projectId: "proj-harmony-rp-voting",
      assetId: "asset-harmony-rp-voting",
      exposureType: TreasuryExposureType.TREASURY_SPONSORSHIP,
      riskTier: RiskTier.TIER_3_REVENUE_GENERATING,
      requestedAmount: 12000,
      approvedAmount: 0,
      consumedAmount: 0,
      currency: "USD",
      treasurySource: "mock://treasury/pending-review",
      expectedReturn: "plugin service revenue and DAO federation utility",
      repaymentModel: "pending governance review",
      status: TreasuryExposureStatus.UNDER_REVIEW,
      governanceReference: governanceCompatible,
      createdAt: now,
      updatedAt: now
    }
  ],
  revenueRecords: [
    {
      id: "rev-dex-country",
      projectId: "proj-dex-country",
      assetId: "asset-dex-country",
      source: RevenueSource.INFRASTRUCTURE,
      status: RevenueStatus.CLASSIFIED,
      grossAmount: 18500,
      netAmount: 14800,
      currency: "USD",
      treasuryShare: 5200,
      daoShare: 0,
      builderShare: 2800,
      maintainerShare: 2400,
      debentureShare: 3200,
      reserveShare: 1200,
      createdAt: now,
      updatedAt: now
    },
    {
      id: "rev-vault-country",
      projectId: "proj-vault-country",
      assetId: "asset-vault-country",
      source: RevenueSource.TREASURY_ASSET,
      status: RevenueStatus.ROUTING_APPROVED,
      grossAmount: 9200,
      netAmount: 7600,
      currency: "USD",
      treasuryShare: 4200,
      daoShare: 0,
      builderShare: 1200,
      maintainerShare: 1600,
      debentureShare: 0,
      reserveShare: 600,
      createdAt: now,
      updatedAt: now
    }
  ],
  plugins: [
    {
      id: "plugin-harmony-rp-voting",
      pluginName: "Harmony RP Voting",
      targetDaoId: "id-harmony-dao",
      pluginType: PluginType.GOVERNANCE,
      status: PluginStatus.GOVERNANCE_REVIEW,
      projectId: "proj-harmony-rp-voting",
      assetId: "asset-harmony-rp-voting",
      fundingId: "fund-harmony-rp-voting",
      governanceScope: "Harmony DAO governance plugin review",
      executionScope: "GOVERNANCE_REVIEW_REQUIRED",
      maintenanceOwner: "id-axodus-core",
      telemetryProfile: telemetryProfile("tp-plugin-harmony-rp", "DAO_SCOPED"),
      createdAt: now,
      updatedAt: now
    },
    {
      id: "plugin-delegation-voting",
      pluginName: "Delegation Voting",
      targetDaoId: "id-harmony-dao",
      pluginType: PluginType.GOVERNANCE,
      status: PluginStatus.DEVELOPMENT,
      projectId: "proj-harmony-rp-voting",
      assetId: "asset-harmony-rp-voting",
      fundingId: "fund-harmony-rp-voting",
      governanceScope: "Delegated local governance support",
      executionScope: "MOCK_ONLY",
      maintenanceOwner: "id-axodus-core",
      telemetryProfile: telemetryProfile("tp-plugin-delegation", "DAO_SCOPED"),
      createdAt: now,
      updatedAt: now
    },
    {
      id: "plugin-native-token-voting",
      pluginName: "Native Token Voting",
      targetDaoId: "id-harmony-dao",
      pluginType: PluginType.GOVERNANCE,
      status: PluginStatus.CLASSIFICATION,
      projectId: "proj-harmony-rp-voting",
      assetId: "asset-harmony-rp-voting",
      fundingId: "fund-harmony-rp-voting",
      governanceScope: "Native governance asset voting model",
      executionScope: "MOCK_ONLY",
      maintenanceOwner: "id-axodus-core",
      telemetryProfile: telemetryProfile("tp-plugin-native-token", "DAO_SCOPED"),
      createdAt: now,
      updatedAt: now
    }
  ],
  acsRuntimes: [
    {
      id: "acs-business-intake",
      ownerId: "id-axodus-core",
      relatedProjectId: "proj-governance-country",
      runtimeType: ACSRuntimeType.INTAKE_ASSISTANT,
      isolationProfile: ACSIsolationProfile.SHARED,
      status: ACSRuntimeStatus.MONITORED,
      permissionProfile: ["CLASSIFY_REQUESTS", "SUMMARIZE_RUNTIME", "NO_EXECUTION"],
      telemetryProfile: telemetryProfile("tp-acs-business-intake", "INTERNAL"),
      computeProfile: { mockCpuUnits: 2, mockMemoryMb: 2048, mockMonthlyBudget: { amount: 300, currency: "USD" } },
      createdAt: now,
      updatedAt: now
    },
    {
      id: "acs-enterprise-sample",
      ownerId: "id-enterprise-sample",
      relatedProjectId: "proj-enterprise-acs",
      runtimeType: ACSRuntimeType.ENTERPRISE_ACS_RUNTIME,
      isolationProfile: ACSIsolationProfile.DEDICATED,
      status: ACSRuntimeStatus.REQUESTED,
      permissionProfile: ["READ_ENTERPRISE_SCOPE", "NO_PROVISIONING", "NO_EXECUTION"],
      telemetryProfile: telemetryProfile("tp-acs-enterprise-sample", "ENTERPRISE_SCOPED"),
      computeProfile: { mockCpuUnits: 4, mockMemoryMb: 4096, mockMonthlyBudget: { amount: 900, currency: "USD" } },
      createdAt: now,
      updatedAt: now
    }
  ],
  acsReceipts: [
    {
      id: "receipt-business-intake-1",
      acsRuntimeId: "acs-business-intake",
      actionType: "MOCK_REQUEST_CLASSIFICATION",
      relatedProjectId: "proj-governance-country",
      relatedAssetId: "asset-governance-country",
      relatedRequestId: "req-governance-country",
      actor: "acs-business-intake",
      timestamp: now,
      inputReference: "mock://requests/req-governance-country",
      outputReference: "mock://receipts/receipt-business-intake-1",
      confidenceLevel: 0.88,
      humanReviewRequired: true,
      governanceReference: governanceCompatible
    }
  ],
  telemetryEvents: [
    {
      id: "tel-request-enterprise-acs",
      eventType: TelemetryEventType.REQUEST_SUBMITTED,
      severity: TelemetrySeverity.NOTICE,
      sourceSystem: "business-runtime-mock",
      relatedRequestId: "req-enterprise-acs",
      relatedEnterpriseId: "id-enterprise-sample",
      status: "visible",
      actor: "id-enterprise-sample",
      payload: { acsRequired: true, provisioningEnabled: false },
      auditReference: "mock://audit/tel-request-enterprise-acs",
      createdAt: now
    },
    {
      id: "tel-dex-treasury-exposure",
      eventType: TelemetryEventType.TREASURY_EXPOSURE_UPDATED,
      severity: TelemetrySeverity.WARNING,
      sourceSystem: "business-runtime-mock",
      relatedProjectId: "proj-dex-country",
      relatedAssetId: "asset-dex-country",
      status: "exposure-visible",
      actor: "id-axodus-core",
      payload: { approvedAmount: 150000, consumedAmount: 40000 },
      governanceReference: governanceCompatible,
      treasuryReference: "tex-dex-country",
      auditReference: "mock://audit/tel-dex-treasury-exposure",
      createdAt: now
    },
    {
      id: "tel-acs-intake-monitored",
      eventType: TelemetryEventType.ACS_RUNTIME_UPDATED,
      severity: TelemetrySeverity.INFO,
      sourceSystem: "business-runtime-mock",
      relatedProjectId: "proj-governance-country",
      relatedAssetId: "asset-governance-country",
      status: "monitored",
      actor: "acs-business-intake",
      payload: { runtimeStatus: ACSRuntimeStatus.MONITORED, provisioningEnabled: false },
      auditReference: "mock://audit/tel-acs-intake-monitored",
      createdAt: now
    },
    {
      id: "tel-revenue-dex",
      eventType: TelemetryEventType.REVENUE_ROUTING_UPDATED,
      severity: TelemetrySeverity.NOTICE,
      sourceSystem: "business-runtime-mock",
      relatedProjectId: "proj-dex-country",
      relatedAssetId: "asset-dex-country",
      status: "routing-visible",
      actor: "id-axodus-core",
      payload: { grossAmount: 18500, distributionExecuted: false },
      auditReference: "mock://audit/tel-revenue-dex",
      createdAt: now
    }
  ]
};
